import { useState } from "react";
import {
  Tabs,
  Text,
  Group,
  Collapse,
  UnstyledButton,
  Paper,
  ScrollArea,
  Stack,
  Box,
  Menu,
} from "@mantine/core";
import {
  IconPlus,
  IconChevronRight,
  IconFile,
  IconFileText,
  IconCopy,
  IconTrash,
  IconDotsVertical,
} from "@tabler/icons-react";
import { Badge, Button } from "@onengine/component-library";
import { useFormStore } from "@/store/form-builder";
import { fieldTypes, FIELD_CATEGORIES } from "@/components/features/FormBuilder/data/fieldTypes";
import { BUILDER_LABELS } from "@/components/features/FormBuilder/data/builderLabels";
import { useIsNarrowEditor } from "@/components/features/FormBuilder/hooks/useIsNarrowEditor";
import { resolveAddTarget } from "./resolveAddTarget";
import styles from "./LeftPanel.module.scss";

export function LeftPanel() {
  return (
    <Paper
      w="100%"
      h="100%"
      style={{
        flexShrink: 0,
        borderRight: "1px solid #cfd4dd",
        overflow: "hidden",
        borderRadius: 0,
      }}
      bg="var(--neutral-300,#f8f8fa)"
    >
      <Tabs
        defaultValue="fields"
        // variant="unstyled"
        color={"var(--bg-brand-default)"}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Tabs.List
          grow
          style={{ borderBottom: "1px solid #cfd4dd", background: "white", height: 40 }}
        >
          <Tabs.Tab value="fields" style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
            Fields
          </Tabs.Tab>
          <Tabs.Tab value="pages" style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
            Pages
          </Tabs.Tab>
        </Tabs.List>
        <ScrollArea style={{ flex: 1 }}>
          <Tabs.Panel value="fields">
            <FieldsTab />
          </Tabs.Panel>
          <Tabs.Panel value="pages">
            <PagesTab />
          </Tabs.Panel>
        </ScrollArea>
      </Tabs>
    </Paper>
  );
}

function FieldsTab() {
  const isNarrow = useIsNarrowEditor();
  const pages = useFormStore((s) => s.pages);
  const currentPageIndex = useFormStore((s) => s.currentPageIndex);
  const selectedSectionId = useFormStore((s) => s.selectedSectionId);
  const setCurrentPageIndex = useFormStore((s) => s.setCurrentPageIndex);
  const setSelectedSectionId = useFormStore((s) => s.setSelectedSectionId);
  const addQuestion = useFormStore((s) => s.addQuestion);

  const handleAdd = (fieldId: string) => {
    const target = resolveAddTarget(pages, currentPageIndex, selectedSectionId);
    if (!target) return;
    if (target.pageIndex !== currentPageIndex) setCurrentPageIndex(target.pageIndex);
    addQuestion({ pageId: target.pageId, sectionId: target.sectionId }, fieldId);
    // FormEditor expands and scrolls to a section only when the selection changes, so
    // re-select a section the user had collapsed by hand. The deferred set lets the null
    // commit first; setting both in one tick would batch into "no change".
    if (selectedSectionId === target.sectionId) {
      setSelectedSectionId(null);
      setTimeout(() => setSelectedSectionId(target.sectionId), 0);
    } else {
      setSelectedSectionId(target.sectionId);
    }
    const questionId = useFormStore.getState().selectedQuestionId;
    // Not folded into FormEditor's selection effect: that effect keys on the section, and the
    // new question's node only exists once the section has re-expanded, so the scroll waits
    // for that render rather than reacting to a store change.
    setTimeout(() => {
      document
        .querySelector(`[data-question-id="${questionId}"]`)
        ?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }, 50);
  };

  return (
    <Stack gap="md" p="sm">
      <Text size="xs" c="var(--text-weak)" className={styles.paletteLabel}>
        {isNarrow ? BUILDER_LABELS.paletteHintTouch : BUILDER_LABELS.paletteHint}
      </Text>
      {FIELD_CATEGORIES.map((cat) => (
        <Stack key={cat} gap={6}>
          <Text
            size="xs"
            fw={600}
            c="var(--text-weak)"
            tt="uppercase"
            lts="0.05em"
            className={styles.paletteLabel}
          >
            {cat}
          </Text>
          <Stack gap={4}>
            {fieldTypes
              .filter((f) => f.category === cat)
              .map((field) => {
                const Icon = field.icon;
                const disabled = !!field.comingSoon;
                return (
                  <Paper
                    key={field.id}
                    p="xs"
                    radius="md"
                    withBorder
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-disabled={disabled}
                    draggable={!disabled}
                    onClick={disabled ? undefined : () => handleAdd(field.id)}
                    onKeyDown={
                      disabled
                        ? undefined
                        : (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleAdd(field.id);
                            }
                          }
                    }
                    onDragStart={
                      disabled
                        ? undefined
                        : (e) => {
                            e.dataTransfer.setData(
                              "application/json",
                              JSON.stringify({ id: field.id, name: field.label }),
                            );
                            e.dataTransfer.effectAllowed = "copy";
                          }
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.6 : 1,
                      borderColor: "#e2e6eb",
                    }}
                    className={styles.fieldTypeClass}
                  >
                    <Icon size={15} />
                    <Text size="sm" c={disabled ? "dimmed" : "#363d4d"}>
                      {field.label}
                    </Text>
                    {disabled && (
                      <Box ml="auto">
                        <Badge color="neutral" variant="plain" size="small">
                          Soon
                        </Badge>
                      </Box>
                    )}
                  </Paper>
                );
              })}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function PagesTab() {
  const {
    pages,
    currentPageIndex,
    setCurrentPageIndex,
    selectedSectionId,
    setSelectedSectionId,
    addPage,
    deletePage,
    clonePage,
    addSection,
    deleteSection,
    duplicateSection,
  } = useFormStore();
  const [openPages, setOpenPages] = useState<Record<string, boolean>>({ "page-1": true });

  return (
    <Stack gap={0} h="100%">
      <Group justify="space-between" px="md" py={8} style={{ borderBottom: "1px solid #f0f2f5" }}>
        <Text className={styles.pagesHeader}>Your Pages</Text>
        <Button
          type="icon"
          variant="tertiary"
          iconLeft={<IconPlus size={16} />}
          onClick={addPage}
          size="base"
          htmlAttributes={{ "aria-label": BUILDER_LABELS.addPage }}
        />
      </Group>
      <ScrollArea style={{ flex: 1 }}>
        <Stack gap={6} p="sm">
          {pages.map((pg, pi) => (
            <Stack key={pg.id} gap={2}>
              <Group gap={2}>
                <UnstyledButton
                  onClick={() => {
                    setCurrentPageIndex(pi);
                    setOpenPages((p) => ({ ...p, [pg.id]: !p[pg.id] }));
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    background: currentPageIndex === pi ? "var (--neutral-400)" : "transparent",
                    borderRadius: 8,
                  }}
                >
                  <IconFile
                    size={16}
                    color={
                      currentPageIndex === pi ? "var(--text-strong)" : "var(--neutral-1400,#646d7e)"
                    }
                  />
                  <Box
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      size="sm"
                      fw={500}
                      c={currentPageIndex === pi ? "var(--text-strong)" : "var(--text-default)"}
                      truncate
                    >
                      {pi + 1}: {pg.name}
                    </Text>
                    <IconChevronRight
                      size={16}
                      color="#646d7e"
                      style={{
                        transform: openPages[pg.id] ? "rotate(90deg)" : "none",
                        transition: "transform 0.15s",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                </UnstyledButton>
                <Menu shadow="md" width={140} position="bottom-end">
                  <Menu.Target>
                    <Box style={{ cursor: "pointer" }}>
                      <IconDotsVertical size={16} />
                    </Box>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconCopy size={14} />}
                      onClick={() => clonePage(pg.id)}
                    >
                      Duplicate
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      disabled={pages.length <= 1}
                      onClick={() => deletePage(pg.id)}
                      style={{
                        opacity: pages.length <= 1 ? 0.5 : 1,
                        color: pages.length <= 1 ? "#fa5252" : undefined,
                      }}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
              <Collapse in={!!openPages[pg.id]}>
                <Stack gap={6} pl={24} mt={2}>
                  {pg.sections.map((sc) => {
                    const isSectionSelected = selectedSectionId === sc.id;
                    return (
                      <div
                        key={sc.id}
                        className={styles.sectionRow}
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <UnstyledButton
                          onClick={() => {
                            setCurrentPageIndex(pi);
                            setSelectedSectionId(sc.id);
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px",
                            borderRadius: 8,
                            background: isSectionSelected ? "#e8f0fe" : undefined,
                          }}
                          className={styles.sectionRowTitle}
                        >
                          <IconFileText
                            size={16}
                            color={isSectionSelected ? "#004ddb" : "#646d7e"}
                          />
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text
                              size="sm"
                              c={isSectionSelected ? "#004ddb" : "#363d4d"}
                              fw={isSectionSelected ? 600 : 500}
                              truncate
                            >
                              {sc.name}
                            </Text>
                          </Box>
                        </UnstyledButton>
                        <Menu shadow="md" width={140} position="bottom-end">
                          <Menu.Target>
                            <Box className={styles.sectionMenu}>
                              <IconDotsVertical size={16} />
                            </Box>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconCopy size={14} />}
                              onClick={() => duplicateSection(pg.id, sc.id)}
                            >
                              Duplicate
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              color="red"
                              disabled={pg.sections.length <= 1}
                              onClick={() => deleteSection(pg.id, sc.id)}
                              style={{
                                opacity: pg.sections.length <= 1 ? 0.5 : 1,
                                color: pg.sections.length <= 1 ? "#fa5252" : undefined,
                              }}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </div>
                    );
                  })}
                  <UnstyledButton
                    onClick={() => addSection(pg.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 8px",
                      borderRadius: 6,
                      fontSize: 12,
                      color: "#004ddb",
                      fontWeight: 500,
                    }}
                  >
                    <IconPlus size={12} /> Add Section
                  </UnstyledButton>
                </Stack>
              </Collapse>
            </Stack>
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
